import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../api";
import headerBanner from "../assets/clear-sky-banner-wide.jpg";

export default function Header({ showLogout = false }) {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  // Fetch user profile when logged in
  useEffect(() => {
    if (showLogout) {
      authFetch("/profile")
        .then((data) => setUserProfile(data))
        .catch((err) => console.error("Error fetching profile:", err));
    }
  }, [showLogout]);

  // Fetch sync status
  useEffect(() => {
    const fetchSyncStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/sync-status`);
        const data = await response.json();
        setSyncStatus(data);
      } catch (err) {
        console.error("Error fetching sync status:", err);
      }
    };
    fetchSyncStatus();
  }, []);

  // Calculate next sync time
  const getNextSyncTime = () => {
    if (!syncStatus?.cron_schedule) return null;
    const now = new Date();
    const currentHourUTC = now.getUTCHours();
    const schedule = syncStatus.cron_schedule; // [0, 8, 16] UTC

    // Find next scheduled hour
    let nextHourUTC = schedule.find(h => h > currentHourUTC);
    let daysToAdd = 0;
    if (nextHourUTC === undefined) {
      // Wrap to tomorrow's first schedule
      nextHourUTC = schedule[0];
      daysToAdd = 1;
    }

    const nextSync = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + daysToAdd,
      nextHourUTC,
      0,
      0
    ));
    return nextSync;
  };

  // Format time ago
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return "Unknown";
    const date = new Date(dateStr);
    if (isNaN(date)) return "Unknown";
    const diffMs = Date.now() - date.getTime();

    // A timestamp in the FUTURE is not "-58779m ago".
    //
    // `diffMins < 60` is true for every negative number, so the first branch caught them and
    // printed the minus sign. The header showed exactly that when last_sync was MAX(created_
    // date) and one listing carried a December date a broker had typed. That source is fixed
    // (it reads ingested_at now), but a clock skew between the browser and the server is
    // enough to make this negative by a minute or two, and "-1m ago" would still be wrong.
    if (diffMs < 0) return "just now";

    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMs / 3600000);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffMs / 86400000)}d ago`;
  };

  // Format time until
  const formatTimeUntil = (date) => {
    if (!date) return "Unknown";
    const diffMs = date.getTime() - Date.now();
    // Same shape as formatTimeAgo above: `diffMins < 60` is true for negatives, so a slot
    // that has just passed would read "in -3m". getNextSyncTime should always be ahead, but
    // "should" is doing the work there, and the cost of being wrong is a visible minus sign.
    if (diffMs <= 0) return "due now";
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `in ${diffMins}m`;
    return `in ${Math.floor(diffMs / 3600000)}h`;
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    gap: "20px",
    backgroundColor: "#000",
    // The artwork is the bar, not an object sitting in it. As a contained block it read as a
    // pasted rectangle: its sky is rgb(14,14,14)-(39,39,39), not the #000 of the band, so the
    // top edge showed however well the sides were faded -- and it left a wide dead black
    // expanse either side of a small picture. Full-bleed there is no edge to see and no empty
    // space to explain, which is exactly why it works on the client page.
    backgroundImage: `url(${headerBanner})`,
    backgroundSize: "cover",
    backgroundPosition: "center 38%",
    color: "#fff",
    minHeight: "150px",
    padding: "16px 40px",
    boxSizing: "border-box",
    boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
  };

  const leftSectionStyle = {
    width: "28%",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  };

  const centerSectionContainerStyle = {
    width: "44%",
    display: "flex",
    justifyContent: "center",
  };

  const rightSectionContainerStyle = {
    width: "28%",
    display: "flex",
    justifyContent: "flex-end",
  };


  const centerSectionStyle = {
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  };



  const rightSectionStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    justifyContent: "flex-end",
    flexWrap: "wrap",
  };

  const avatarStyle = {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    objectFit: "cover",
    border: "2px solid #fff",
  };

  const avatarPlaceholderStyle = {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "#555",
    border: "2px solid #fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: "600",
    color: "#fff",
  };

  const greetingStyle = {
    fontSize: "14px",
    fontWeight: "500",
    color: "#fff",
  };

  return (
    <header style={headerStyle}>
      {/* DIV 1 - Left section: 33.3% width - Logo and Profile */}
      <div style={leftSectionStyle}>
        {/* Left logo div */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {syncStatus && (
            <div style={{ fontSize: '12px', color: '#FFF', marginTop: '4px' }}>
              <div>Last data update: {formatTimeAgo(syncStatus.last_sync)}</div>
              <div>Next data update: {formatTimeUntil(getNextSyncTime())}</div>
              <div style={{ color: '#FFF', fontSize: '12px', marginTop: '2px' }}>
                24h: {syncStatus.new_24h || 0} new, {syncStatus.updated_24h || 0} updated
              </div>
            </div>
          )}
        </div>
        {/* Profile pic and name div */}
        {userProfile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {userProfile.profile_picture_url ? (
              <img
                src={userProfile.profile_picture_url}
                alt="Profile"
                style={avatarStyle}
              />
            ) : (
              <div style={avatarPlaceholderStyle}>
                {userProfile.first_name
                  ? userProfile.first_name.charAt(0).toUpperCase()
                  : userProfile.email.charAt(0).toUpperCase()}
              </div>
            )}
            <span style={greetingStyle}>
              Hello, {userProfile.first_name || userProfile.email.split('@')[0]}
            </span>
          </div>
        )}
      </div>

      {/* DIV 2 - Center section: 33.3% width - Title
          Empty of content: the ClearSky mark is centred in the background artwork behind this
          column, so a second copy here would double it. The div IS the click-through to the
          pipeline that the old "BEST DEAL FINDER" title carried.

          Sized explicitly, and that is the whole point. It used to be an auto-width flex item
          wrapping a `width: 100%` child -- and a percentage width resolves against a
          containing block that is itself shrink-to-fit, which makes it count as zero. The
          column collapsed to 0px wide, so the logo stopped being clickable entirely and the
          only way back to the pipeline was the hamburger menu. */}
      <div style={centerSectionContainerStyle}>
        <div
          style={{ ...centerSectionStyle, cursor: "pointer", width: "100%", minHeight: "110px" }}
          onClick={() => navigate("/")}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/"); }}
          role="link"
          tabIndex={0}
          title="Pipeline"
          aria-label="ClearSky — pipeline"
        />
      </div>

      {/* DIV 3 - Right section: 33.3% width - Menu */}
      <div style={rightSectionContainerStyle}>
        <div style={rightSectionStyle}>
          {showLogout && (
            <div style={{ position: "relative" }}>
              <MenuButton
                isOpen={menuOpen}
                onClick={() => setMenuOpen(!menuOpen)}
              />

              {menuOpen && (
                <>
                  {/* Invisible overlay to close menu when clicking outside */}
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 999,
                    }}
                    onClick={() => setMenuOpen(false)}
                  />
                  {/* Dropdown menu */}
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      right: 0,
                      marginTop: "8px",
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                      minWidth: "180px",
                      zIndex: 1000,
                      overflow: "hidden",
                    }}
                  >
                    <MenuItem
                      label="Pipeline"
                      onClick={() => {
                        navigate("/pipeline");
                        setMenuOpen(false);
                      }}
                    />
                    <MenuItem
                      label="Saved Search Alerts"
                      onClick={() => {
                        navigate("/alerts");
                        setMenuOpen(false);
                      }}
                    />
                    {userProfile?.role === "admin" && (
                      <MenuItem
                        label="Clients"
                        onClick={() => {
                          navigate("/clients");
                          setMenuOpen(false);
                        }}
                      />
                    )}
                    <MenuItem
                      label="Investors"
                      onClick={() => {
                        navigate("/investors");
                        setMenuOpen(false);
                      }}
                    />
                    <MenuItem
                      label="Buildings"
                      onClick={() => {
                        navigate("/buildings");
                        setMenuOpen(false);
                      }}
                    />
                    <MenuItem
                      label="Listings"
                      onClick={() => {
                        navigate("/listings");
                        setMenuOpen(false);
                      }}
                    />
                    <MenuItem
                      label="Sponsor Deals"
                      onClick={() => {
                        navigate("/sponsor-deals");
                        setMenuOpen(false);
                      }}
                    />
                    <MenuItem
                      label="Data Quality"
                      onClick={() => {
                        navigate("/data-quality");
                        setMenuOpen(false);
                      }}
                    />
                    <MenuItem
                      label="Units"
                      onClick={() => {
                        navigate("/units");
                        setMenuOpen(false);
                      }}
                    />
                    <MenuItem
                      label="Unit Merge"
                      onClick={() => {
                        navigate("/unit-merge");
                        setMenuOpen(false);
                      }}
                    />
                    <MenuItem
                      label="MIS"
                      onClick={() => {
                        navigate("/mis");
                        setMenuOpen(false);
                      }}
                    />
                    <MenuItem
                      label="Zones"
                      onClick={() => {
                        navigate("/zones");
                        setMenuOpen(false);
                      }}
                    />
                    <MenuItem
                      label="Rental Stats"
                      onClick={() => {
                        navigate("/rental-stats");
                        setMenuOpen(false);
                      }}
                    />
                    <MenuItem
                      label="Coverage"
                      onClick={() => {
                        navigate("/coverage");
                        setMenuOpen(false);
                      }}
                    />
                    {userProfile?.role === "admin" && (
                      <MenuItem
                        label="Docs"
                        onClick={() => {
                          navigate("/docs");
                          setMenuOpen(false);
                        }}
                      />
                    )}
                    <MenuDivider />
                    <MenuItem
                      label="Profile"
                      onClick={() => {
                        navigate("/profile");
                        setMenuOpen(false);
                      }}
                    />
                    <MenuItem
                      label="Settings"
                      onClick={() => {
                        navigate("/settings");
                        setMenuOpen(false);
                      }}
                    />
                    {userProfile?.role === "admin" && (
                      <MenuItem
                        label="Users"
                        onClick={() => {
                          navigate("/users");
                          setMenuOpen(false);
                        }}
                      />
                    )}
                    <MenuDivider />
                    <MenuItem
                      label="Logout"
                      onClick={() => {
                        handleLogout();
                        setMenuOpen(false);
                      }}
                      danger
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function MenuItem({ label, onClick, danger = false }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      style={{
        display: "block",
        width: "100%",
        padding: "12px 16px",
        textAlign: "left",
        border: "none",
        backgroundColor: hovered ? (danger ? "#fee" : "#f5f5f5") : "transparent",
        color: danger ? "#c00" : "#333",
        fontSize: "14px",
        cursor: "pointer",
        transition: "background-color 0.15s ease",
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {label}
    </button>
  );
}

function MenuDivider() {
  return (
    <div
      style={{
        height: "1px",
        backgroundColor: "#eee",
        margin: "4px 0",
      }}
    />
  );
}

function MenuButton({ isOpen, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      style={{
        backgroundColor: hovered ? "#fff" : "transparent",
        border: "2px solid #fff",
        color: hovered ? "#000" : "#fff",
        padding: "10px",
        borderRadius: "4px",
        cursor: "pointer",
        transition: "all 0.3s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        width: "40px",
        height: "40px",
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label="Menu"
    >
      <span
        style={{
          display: "block",
          width: "18px",
          height: "2px",
          backgroundColor: hovered ? "#000" : "#fff",
          transition: "all 0.3s ease",
          transform: isOpen ? "rotate(45deg) translateY(6px)" : "none",
        }}
      />
      <span
        style={{
          display: "block",
          width: "18px",
          height: "2px",
          backgroundColor: hovered ? "#000" : "#fff",
          transition: "all 0.3s ease",
          opacity: isOpen ? 0 : 1,
        }}
      />
      <span
        style={{
          display: "block",
          width: "18px",
          height: "2px",
          backgroundColor: hovered ? "#000" : "#fff",
          transition: "all 0.3s ease",
          transform: isOpen ? "rotate(-45deg) translateY(-6px)" : "none",
        }}
      />
    </button>
  );
}